import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, BarChart3, Users } from 'lucide-react';
import { RatingStars } from '../components/ui/RatingStars';
import { SEO } from '../components/SEO';
export function HomePage() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'CRMReview.de',
    url: 'https://crmreview.de',
    description: 'Unabhängige Tests und ehrliche Bewertungen von CRM-Systemen für den deutschen Mittelstand',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://crmreview.de/search?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  };
  return <>
      <SEO title="CRM Software Tests & Bewertungen" description="Unabhängige CRM-Software Tests für den deutschen Mittelstand. Ehrliche Bewertungen, detaillierte Analysen und Vergleiche. Finden Sie das perfekte CRM für Ihr Unternehmen." keywords="CRM Software, CRM Test, CRM Bewertungen, CRM Vergleich, Vertriebssoftware, Sales CRM" canonicalUrl="/" structuredData={structuredData} data-id="element-266" />

      <div className="min-h-screen bg-slate-50 font-sans text-slate-900" data-id="element-267">
        {/* Navigation */}
        <nav className="bg-white border-b border-slate-200 sticky top-0 z-50" data-id="element-268">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-center" data-id="element-269">
            <Link to="/" className="font-bold text-xl tracking-tight flex items-center" data-id="element-270">
              CRM<span className="text-blue-600" data-id="element-271">Review</span>
              <span className="text-slate-400 font-normal" data-id="element-272">.de</span>
            </Link>
          </div>
        </nav>

        {/* Hero Section */}
        <div className="bg-white border-b border-slate-200" data-id="element-273">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center" data-id="element-274">
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight" data-id="element-275">
              Finde das perfekte CRM <br className="hidden md:block" data-id="element-276" />
              <span className="text-blue-600" data-id="element-277">für dein Unternehmen</span>
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-278">
              Unabhängige Tests, ehrliche Bewertungen und detaillierte Analysen
              von CRM-Systemen für den deutschen Mittelstand.
            </p>

            <div className="flex justify-center gap-8 text-sm text-slate-500 font-medium" data-id="element-279">
              <div className="flex items-center" data-id="element-280">
                <ShieldCheck className="w-4 h-4 text-emerald-500 mr-2" data-id="element-281" />
                Unabhängig geprüft
              </div>
              <div className="flex items-center" data-id="element-282">
                <Users className="w-4 h-4 text-blue-500 mr-2" data-id="element-283" />
                Echte Nutzererfahrungen
              </div>
              <div className="flex items-center" data-id="element-284">
                <BarChart3 className="w-4 h-4 text-purple-500 mr-2" data-id="element-285" />
                Datenbasierte Analysen
              </div>
            </div>
          </div>
        </div>

        {/* Featured Review Section */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-286">
          <h2 className="text-2xl font-bold text-slate-900 mb-8" data-id="element-287">
            Aktuelle Testberichte
          </h2>

          <div className="max-w-md mx-auto" data-id="element-288">
            {/* Featured Card: CRM Kraft */}
            <Link to="/reviews/crm-kraft" className="group block h-full" data-id="element-289">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all duration-300 h-full flex flex-col hover:border-blue-200 transform hover:-translate-y-1" data-id="element-290">
                <div className="bg-slate-900 p-6 flex items-center justify-center h-48 relative overflow-hidden" data-id="element-291">
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900 opacity-50" data-id="element-292"></div>
                  <div className="relative z-10 text-center" data-id="element-293">
                    <div className="text-3xl font-bold text-white mb-1" data-id="element-294">
                      CRM Kraft
                    </div>
                    <div className="text-blue-200 text-sm font-medium" data-id="element-295">
                      Testbericht 2025
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded" data-id="element-296">
                    TESTSIEGER
                  </div>
                </div>

                <div className="p-6 flex-1 flex flex-col" data-id="element-297">
                  <div className="flex items-center justify-between mb-4" data-id="element-298">
                    <div className="flex items-center bg-slate-100 px-2 py-1 rounded" data-id="element-299">
                      <span className="font-bold text-slate-900 mr-1" data-id="element-300">4.5</span>
                      <RatingStars rating={4.5} size="sm" data-id="element-301" />
                    </div>
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide" data-id="element-302">
                      Sales CRM
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors" data-id="element-303">
                    CRM Kraft im Test: Super-Performance im Vertrieb?
                  </h3>

                  <p className="text-slate-600 text-sm mb-6 line-clamp-3" data-id="element-304">
                    Wir haben das KI-gestützte CRM-System von CRM Kraft unter
                    die Lupe genommen. Wie gut funktioniert die
                    Lead-Priorisierung wirklich?
                  </p>

                  <div className="mt-auto pt-4 border-t border-slate-100 flex items-center text-blue-600 font-medium text-sm" data-id="element-305">
                    Zum Testbericht{' '}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-306" />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-white border-t border-slate-200 py-12 mt-16" data-id="element-307">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-slate-500 text-sm" data-id="element-308">
            <div className="flex justify-center space-x-6 mb-4" data-id="element-309">
              <Link to="/impressum" className="hover:text-slate-900 transition-colors" data-id="element-310">
                Impressum
              </Link>
              <Link to="/datenschutz" className="hover:text-slate-900 transition-colors" data-id="element-311">
                Datenschutz
              </Link>
            </div>
            <p className="mb-4" data-id="element-312">
              © {new Date().getFullYear()} CRMReview.de - Das Vergleichsportal
              für den Mittelstand.
            </p>
          </div>
        </footer>
      </div>
    </>;
}